/**
 * Class-name utilities shared by the upload engine.
 *
 * This module is the single source of truth for:
 *  - detecting which sheet columns hold the class / year / early-access values
 *  - combining a separate year value with a class value into the final class name
 *  - building the class list / class tree that the preview and the payload use
 *
 * Naming rule (agreed with product):
 *  - a class that already embeds its year is left untouched ("Year 1A", "YR-1.1", "1A" with year 1)
 *  - a single-letter class is appended to the year without a separator ("1" + "A" -> "1A", "YR-1" + "A" -> "YR-1A")
 *  - any other class is joined with a dash ("YR-1" + "1" -> "YR-1-1", "7" + "2" -> "7-2")
 */

const YEAR_PREFIX_PATTERN = '(?:fs|year|yr|y|grade|gr|g|class|prekg|kg)?'
const SEPARATOR_PATTERN = '[\\s\\-_.]*'

const CLASS_COLUMN_PATTERNS = [/class/, /\bform\b/, /section/, /classroom/, /\bgroup\b/, /grade/, /\breg\b/]
const YEAR_COLUMN_PATTERNS = [/year/, /grade/, /level/]
const EARLY_ACCESS_COLUMN_PATTERNS = [/upstrive early access/, /early access/, /upstrive early/, /early upstrive/, /upstrive product/, /primary/]

// Keys the upload engine adds to every processed row; never treat them as sheet columns
const CANONICAL_ROW_KEYS = new Set(['classesName', 'rawClassName', 'primary'])

const PRIMARY_VALUES = new Set(['true', 'yes', '1', 'primary', 'early access', 'upstrive early'])

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const toTrimmedString = (value) => (value === undefined || value === null ? '' : String(value).trim())

/**
 * Normalizes class names by removing special characters and standardizing format
 * for consistent comparison
 * @param {string} className - The class name to normalize
 * @returns {string} - The normalized class name
 */
export const normalizeClassName = (className) => {
    if (!className) return ''

    // Convert to string in case it's a number
    const classStr = String(className)

    // Convert to lowercase
    let normalized = classStr.toLowerCase().trim()

    // Replace common variations before removing special characters
    normalized = normalized
        .replace(/pre[-\s]*kg/i, 'prekg')
        .replace(/pre[-\s]*k/i, 'prekg')
        .replace(/kindergarten/i, 'kg')
        .replace(/nursery[-\s]*(school)?/i, 'nursery')
        .replace(/reception/i, 'reception')
        .replace(/grade[-\s]*/i, 'grade')
        .replace(/year[-\s]*/i, 'year')
        .replace(/class[-\s]*/i, 'class')
        .replace(/group[-\s]*/i, 'group')
        .replace(/section[-\s]*/i, 'section')
    // Note: We're not replacing FS with "foundation stage" anymore

    // Remove all special characters and spaces
    normalized = normalized.replace(/[^a-z0-9]/g, '')

    return normalized
}

/**
 * Extracts year information from a class name
 * @param {string} className - The class name to process
 * @returns {Object} - Object containing extracted year and cleaned class name
 */
export const extractYearFromClass = (className) => {
    if (!className) return { year: null, cleanClassName: '' }

    // Keep the original class name for display
    const originalClassName = String(className).trim()

    // Normalize for pattern matching
    const normalized = normalizeClassName(className)

    // Special prefixes with embedded year/level information
    const specialPrefixes = [
        { prefix: 'fs', pattern: /fs(\d+)([a-z]*)/, yearGroup: 1, classGroup: 2 },
        { prefix: 'prekg', pattern: /prekg(\d*)([a-z]*)/, yearGroup: 1, classGroup: 2, defaultYear: 0 },
        { prefix: 'kg', pattern: /kg(\d*)([a-z]*)/, yearGroup: 1, classGroup: 2, defaultYear: 0 },
    ]

    // Check for special prefixes first
    for (const { prefix, pattern, yearGroup, defaultYear } of specialPrefixes) {
        if (normalized.startsWith(prefix)) {
            const match = normalized.match(pattern)
            if (match) {
                const yearPart = match[yearGroup]
                const year = yearPart ? Number.parseInt(yearPart, 10) : defaultYear

                // Return the original class name to preserve formatting
                return {
                    year,
                    cleanClassName: originalClassName,
                }
            }
        }
    }

    // Standard year patterns
    const yearPatterns = [
        // Year followed by letter (Year 1A, Year1A)
        { regex: /year(\d+)([a-z]*)/, yearGroup: 1, classGroup: 2 },

        // Grade followed by number (Grade 1A, Grade1A)
        { regex: /grade(\d+)([a-z]*)/, yearGroup: 1, classGroup: 2 },

        // Just a number with optional letter (1A, 1-A)
        { regex: /^(\d+)([a-z]*)$/, yearGroup: 1, classGroup: 2 },

        // Special cases
        { regex: /nursery|reception/, yearValue: 0, useFullMatch: true },
    ]

    for (const pattern of yearPatterns) {
        const match = normalized.match(pattern.regex)

        if (match) {
            if (pattern.useFullMatch) {
                return {
                    year: pattern.yearValue,
                    cleanClassName: originalClassName,
                }
            }

            const year = Number.parseInt(match[pattern.yearGroup], 10)

            // Return the original class name to preserve formatting
            return {
                year,
                cleanClassName: originalClassName,
            }
        }
    }

    // If no year pattern is found, return the original class with null year
    return { year: null, cleanClassName: originalClassName }
}

/**
 * Reduces a year value to a comparison token. Never use the result for display or storage.
 *  "YR-1", "Y1", "Year 1", "Grade 1", "01", 1 -> "1"
 *  "FS01" -> "fs1"
 *  "Reception" -> "reception"
 * @param {number|string} year
 * @returns {string} - Comparison token, or '' when the year is empty
 */
export const normalizeYear = (year) => {
    const str = toTrimmedString(year).toLowerCase()
    if (!str) return ''

    const fsMatch = str.match(/^fs[\s\-_.]*0*(\d+)$/)
    if (fsMatch) return `fs${Number.parseInt(fsMatch[1], 10)}`

    const numericMatch = str.match(/^(?:year|yr|y|grade|gr|g|class)?[\s\-_.]*0*(\d+)$/)
    if (numericMatch) return String(Number.parseInt(numericMatch[1], 10))

    return str.replace(/[^a-z0-9]/g, '')
}

/**
 * Checks whether a class name already embeds the given year.
 * The year is matched anchored at the start of the class name so that year "1"
 * does not match "10A" or "YR-11-1".
 * @param {string} className - The class name to check
 * @param {number|string} year - The year to check for
 * @returns {boolean} - True if the year is already included in the class name
 */
export const isYearIncludedInClass = (className, year) => {
    const yearToken = normalizeYear(year)
    const classStr = toTrimmedString(className).toLowerCase()
    if (!classStr || !yearToken) return false

    if (/^\d+$/.test(yearToken)) {
        // A class that is only a number ("1") embeds the year only when the year is written
        // the same way ("1" + "1" -> "1"); "YR-1" + "1" is a section and must be joined.
        if (/^\d+$/.test(classStr)) {
            const yearStr = toTrimmedString(year).toLowerCase()
            return /^\d+$/.test(yearStr) && normalizeYear(classStr) === yearToken
        }
        const numericYearPattern = new RegExp(`^${YEAR_PREFIX_PATTERN}${SEPARATOR_PATTERN}0*${yearToken}(?![0-9])`)
        return numericYearPattern.test(classStr)
    }

    const normalizedClass = normalizeClassName(classStr)
    if (!normalizedClass) return false
    if (normalizedClass === yearToken) return true

    return new RegExp(`^${escapeRegExp(yearToken)}(?![0-9])`).test(normalizedClass)
}

/**
 * Builds the final class name from a separate year value and class value.
 * See the module header for the naming rule.
 * @param {number|string} year - The year value (may be empty)
 * @param {string} className - The class value as written in the sheet
 * @returns {string} - The resolved class name ('' when there is no class value)
 */
export const resolveClassName = (year, className) => {
    const classStr = toTrimmedString(className)
    const yearStr = toTrimmedString(year)

    if (!classStr) return ''
    if (!yearStr) return classStr
    if (isYearIncludedInClass(classStr, yearStr)) return classStr

    if (/^[a-z]$/i.test(classStr)) {
        return `${yearStr}${classStr.toUpperCase()}`
    }

    return `${yearStr}-${classStr}`
}

/**
 * Interprets an "early access" cell value
 * @param {*} value
 * @returns {boolean}
 */
export const isPrimaryValue = (value) => {
    if (value === true) return true
    if (value === undefined || value === null || value === false) return false
    return PRIMARY_VALUES.has(String(value).toLowerCase().trim())
}

const findColumn = (keys, patterns, isExcluded = () => false) => {
    for (const pattern of patterns) {
        const found = keys.find((key) => pattern.test(key.toLowerCase()) && !isExcluded(key))
        if (found) return found
    }
    return null
}

const looksLikeClassValue = (value) => {
    if (value === undefined || value === null || value === '') return false
    const strValue = String(value).toLowerCase().trim()
    return (
        strValue.includes('grade') ||
        strValue.includes('year') ||
        strValue.includes('class') ||
        strValue.includes('fs') ||
        /^\d+[a-z]?$/i.test(strValue) ||
        /^[a-z]$/i.test(strValue) ||
        /^(kg|prekg|nursery|reception)/i.test(strValue)
    )
}

/**
 * Detects which columns of a student sheet hold the class, year and early-access values.
 * Works on raw sheet rows and on processed rows (canonical keys are ignored).
 * @param {Array<Object>} rows - Student rows
 * @returns {{ classColumn: string|null, yearColumn: string|null, earlyAccessColumn: string|null, classColumnGuessed: boolean }}
 */
export const detectStudentColumns = (rows) => {
    const result = { classColumn: null, yearColumn: null, earlyAccessColumn: null, classColumnGuessed: false }
    if (!Array.isArray(rows) || rows.length === 0 || !rows[0] || typeof rows[0] !== 'object') return result

    const keys = Object.keys(rows[0]).filter((key) => !CANONICAL_ROW_KEYS.has(key))

    result.classColumn = findColumn(keys, CLASS_COLUMN_PATTERNS, (key) => key.toLowerCase().includes('year'))
    result.yearColumn = findColumn(keys, YEAR_COLUMN_PATTERNS, (key) => key === result.classColumn)
    result.earlyAccessColumn = findColumn(keys, EARLY_ACCESS_COLUMN_PATTERNS)

    if (!result.classColumn) {
        const sampleRows = rows.slice(0, 5)
        const guessed = keys.find((key) => {
            if (key === result.yearColumn || key === result.earlyAccessColumn) return false
            if (/id|name|email|mail|user|gender|password|pass/i.test(key)) return false
            return sampleRows.some((row) => looksLikeClassValue(row[key]))
        })
        if (guessed) {
            result.classColumn = guessed
            result.classColumnGuessed = true
        }
    }

    return result
}

/**
 * Reads the class information of one student row.
 * Processed rows carry the resolved name in `classesName`; raw rows are resolved on the fly.
 * @param {Object} row - Student row
 * @param {{ classColumn: string|null, yearColumn: string|null }} columns - Result of detectStudentColumns
 * @returns {{ className: string, year: string, rawClassName: string }}
 */
export const getStudentClassInfo = (row, columns) => {
    if (!row) return { className: '', year: '', rawClassName: '' }

    const rawValue = columns?.classColumn ? row[columns.classColumn] : ''
    const rawYear = columns?.yearColumn ? row[columns.yearColumn] : ''
    const year = toTrimmedString(rawYear)

    if (typeof row.classesName === 'string') {
        return {
            className: row.classesName.trim(),
            year,
            rawClassName: toTrimmedString(row.rawClassName !== undefined ? row.rawClassName : rawValue),
        }
    }

    return {
        className: resolveClassName(year, rawValue),
        year,
        rawClassName: toTrimmedString(rawValue),
    }
}

/**
 * Lists the distinct classes found in the student rows.
 * @param {Array<Object>} rows - Student rows (raw or processed)
 * @returns {Array<{ name: string, year: string, rawName: string, isPrimary: boolean, studentCount: number }>}
 */
export const extractUploadClasses = (rows) => {
    if (!Array.isArray(rows) || rows.length === 0) return []

    const columns = detectStudentColumns(rows)
    const classes = new Map()

    rows.forEach((row) => {
        if (!row) return
        const { className, year, rawClassName } = getStudentClassInfo(row, columns)
        if (!className) return

        let effectiveYear = year
        if (!effectiveYear) {
            const extracted = extractYearFromClass(className).year
            effectiveYear = extracted === null || extracted === undefined ? '' : String(extracted)
        }

        const isPrimary = row.primary === true || (columns.earlyAccessColumn ? isPrimaryValue(row[columns.earlyAccessColumn]) : false)
        const key = `${isPrimary}|${effectiveYear}|${normalizeClassName(className)}`

        const existing = classes.get(key)
        if (existing) {
            existing.studentCount += 1
            return
        }

        classes.set(key, { name: className, year: effectiveYear, rawName: rawClassName, isPrimary, studentCount: 1 })
    })

    return Array.from(classes.values()).sort((a, b) => a.name.localeCompare(b.name))
}

/**
 * Organizes student data into a tree structure by year and class
 * @param {Array} studentsData - Array of student data objects
 * @returns {Object} - Tree structure of years and classes
 */
export const organizeClassTree = (studentsData) => {
    if (!studentsData || !studentsData.length) return {}

    const columns = detectStudentColumns(studentsData)
    if (!columns.classColumn) {
        return { error: 'Could not identify class column' }
    }

    const classTree = {
        primary: {},
        secondary: {},
    }

    extractUploadClasses(studentsData).forEach((cls) => {
        // Classes without any year information cannot be created by the backend
        if (!cls.year) return

        const treeSection = cls.isPrimary ? classTree.primary : classTree.secondary
        if (!treeSection[cls.year]) {
            treeSection[cls.year] = []
        }

        treeSection[cls.year].push({
            name: cls.name,
            studentCount: cls.studentCount,
            isPrimary: cls.isPrimary,
        })
    })

    Object.keys(classTree.primary).forEach((year) => {
        classTree.primary[year].sort((a, b) => a.name.localeCompare(b.name))
    })

    Object.keys(classTree.secondary).forEach((year) => {
        classTree.secondary[year].sort((a, b) => a.name.localeCompare(b.name))
    })

    return classTree
}
